import { Icon } from '@iconify/react/dist/iconify.js';
import { Box, Chip, Grid, Typography } from '@mui/material';
import React from 'react';

const FetchedStudentDetails = ({ studentData }) => {
  // an EMI student pays in instalments, so the API sends one entry per payment
  // date. A single payment is shown the same way, just without the count.
  const payments = Array.isArray(studentData?.payments) ? studentData.payments : [];
  const hasPayments = payments.length > 0;
  const hasMultiplePayments = payments.length > 1;

  return (
    <>
      <Box
        sx={{
          borderRadius: 2,
          mb: 2,
          overflow: 'hidden',
          boxShadow: ' rgba(0, 0, 0, 0.16) 0px 1px 4px;',
        }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: 2,
          }}
        >
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Student Details
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {studentData?.name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Admission No
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {studentData?.admn_no}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Admission Date
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {studentData?.admission_confirmed_date}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                {hasMultiplePayments ? `Payment Dates (${payments.length})` : 'Payment Date'}
              </Typography>

              {hasPayments ? (
                <Box sx={{ mt: 0.5, maxHeight: 150, overflowY: 'auto', pr: 0.5 }}>
                  {payments.map((payment, index) => (
                    <Box
                      key={`${payment?.payment_date}-${index}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        py: 0.5,
                        borderBottom: index < payments.length - 1 ? '1px dashed rgba(0,0,0,0.12)' : 'none',
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {payment?.payment_date}
                        </Typography>
                        {payment?.fee_types ? (
                          <Typography variant="caption" color="text.secondary">
                            {payment.fee_types}
                          </Typography>
                        ) : null}
                      </Box>
                      <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                        ₹{Number(payment?.amount || 0).toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" fontWeight={500}>
                  {studentData?.payment_date}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                First Session Date
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {studentData?.first_session_date || 'Nill'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Last Session Date
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {studentData?.last_session_date || 'Nill'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Total Sale Amount
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                ₹{studentData?.total_sale_amount}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Required Sessions
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {studentData?.total_required_sessions}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Completed Sessions
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {studentData?.total_sessions_completed}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Account Type
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {studentData?.account_type}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Type
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {studentData?.type}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Fee Per Session
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                ₹{studentData?.fee_per_session}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                OGA Name
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {studentData?.oga_name}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                CRO Name
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {studentData?.cro_name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Emi Student?
              </Typography>
              {studentData?.emi_student == 'Yes' ? (
                <Chip
                  size="small"
                  label="Yes"
                  variant="outlined"
                  color="success"
                  icon={<Icon width={20} height={20} icon={'healthicons:yes'} color="green" />}
                />
              ) : studentData?.emi_student == 'No' ? (
                <Chip
                  size="small"
                  label="No"
                  variant="outlined"
                  color="error"
                  icon={<Icon width={20} height={20} icon={'healthicons:no'} color="red" />}
                />
              ) : studentData?.emi_student == 'Not Available' ? (
                studentData?.emi_student
              ) : (
                'N/A'
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Collected Fee
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {studentData?.collected_fee}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default FetchedStudentDetails;
